import { AbilityAction, AbilitySubject } from '@/abilitiesByRole'
import { can } from '@/ability'
import store from '@/store'
import { KeyDict, Optional } from '@/types'
import { CallTriage } from '@/types/casts'
import { CastStream, StreamType } from '@/types/streams'
import { UserProfile } from '@/types/users'
import { computed, ComputedRef, ref } from '@vue/composition-api'
import { cloneDeep, pickBy } from 'lodash'
import { confirmRemoveCasterFromCast } from '@/modules/casts/utils/castCasters'
import { Cast } from '@/modules/casts/classes'
import { hasCastersMixin } from '@/modules/classBuilder/mixins/hasCasters'
import { WRTCServerType } from '@/types/admin'


const useCastersPalette = (cast: ComputedRef<Cast|null>, teamMembers: ComputedRef<KeyDict<UserProfile>>) => {
  // --- Data ---
  const showEditProfileModal = ref(false)
  const selectedMember= ref<UserProfile|null>(null)
  const needsRepublishing = ref<KeyDict<boolean>>({})

  // --- Computes --- (user related)
  const canModifyCaster = computed(() => can.value(AbilityAction.Edit, AbilitySubject.Cast))
  const currentUserId = computed(() => store.state.user.id)
  const currentUserIsModerator = computed (() => {
    if (!hasCastersMixin(cast.value)) return false
    if (currentUserId.value === null) return false
    return cast.value.isModerator(currentUserId.value)
  })

  // --- Computes --- (others)
  const activeTeam = computed(() => store.state.team.activeTeam)
  const casters = computed(() => {
    const casters = cast.value?.invited_casters ?? {}
    return pickBy(casters, (caster) => ![CallTriage.Rejected, CallTriage.Init].includes(caster.triage))
  })
  const onlineUsers = computed(() => cast.value?.online_sessions ?? {})
  const sourceStreams = computed(() => store.state.events.sourceStreams)
  // FIXME: Typing of the corresponding property in sub-components is inconsitent (or missing)
  const streamsManager = computed(() => store.state.streams.castStreams)
  const token = computed(() => cast.value?.token ?? '')
  const acceptedCasters = computed(() => {
    return pickBy(casters.value, (caster) => {
      if (caster.deleted) return false
      if (caster.anonymous && caster.triage !== CallTriage.Accepted) return false
      return true
    })
  })
  const visibleCasters = computed(() => {
    if (canModifyCaster.value) return acceptedCasters.value
    const userId = currentUserId.value
    if (userId === null || acceptedCasters.value[userId] === undefined) return {}
    return { [userId]: acceptedCasters.value[userId] }
  })
  const isCrewAllowed = computed(() => {
    if (!currentUserId.value) return false
    const allowedRoles = ['admin', 'member']
    const currentUser = getProfile(currentUserId.value)
    if (currentUser && allowedRoles.includes(currentUser.role)) return true
    else return false
  })

  // --- Methods ---
  const getProfile = (id: string): Optional<UserProfile> => teamMembers.value[id]
  const getStream = (id: string): CastStream|null => {
    let usersStream: string|null = null
    for (const [key, sourceStream] of Object.entries(sourceStreams.value)) {
      if (sourceStream.type === StreamType.Caster && sourceStream.user_id === id && !key.includes('broadcast')) {
        usersStream = key
      }
    }
    if (usersStream === null) {
      return null
    }
    const isJanus = cast.value?.wrtcServerType === WRTCServerType.JANUS
    const tmpStreamId = isJanus ? usersStream : usersStream + '_lowrate'
    return streamsManager.value[tmpStreamId] ?? null
  }
  const editProfile = (id: string) => {
    const member = teamMembers.value[id]
    if (member !== undefined) {
      selectedMember.value = member.clone()
      showEditProfileModal.value = true
    }
  }
  const removeCaster = async (casterId: string) => {
    const casterProfile = teamMembers.value[casterId]
    return await confirmRemoveCasterFromCast(casterId, cast.value, casterProfile)
  }
  const isInvitedCaster = (casterId: string) => {
    const caster = cast.value?.invited_casters?.[casterId]
    if (caster === undefined) return false
    return !caster.deleted
  }
  const canBeRemovedAsCaster = (casterId: string) => {
    return isInvitedCaster(casterId)
  }
  const requestCasterRepublish = async (caster: string) => {
    if (cast.value === null) throw new Error('Cannot requestCasterRepublish, currentCast is null')
    await store.dispatch.events.requestCasterRepublish({ cast: cast.value.id, caster })
  }
  const setNeedsRepublishing = (newVal: { user_id: string, val: boolean }) => {
    needsRepublishing.value[newVal.user_id] = newVal.val
  }

  // FIXME: kept for backward compatibility (regular casts), remove in the future
  // Is actually moved to useUserProfile composition
  const updateTeamMember = async (): Promise<boolean> => {
    if (selectedMember.value === null) return false
    const updatedMember: UserProfile = cloneDeep(selectedMember.value)
    try {
      await store.dispatch.team.updateTeamMember({ teamId: activeTeam.value, member: updatedMember })
      showEditProfileModal.value = false
      selectedMember.value = null
      const memberId = updatedMember.id
      if (needsRepublishing.value[memberId] === true && casters.value[memberId] !== undefined) {
        const callTriage = casters.value[memberId].triage
        if ([CallTriage.Unverified, CallTriage.InModeration].includes(callTriage)) {
          await requestCasterRepublish(memberId)
          needsRepublishing.value[memberId] = false
        }
      }
    } catch (error) {
      console.error('Error updateTeamMember', error)
      return false
    }
    return true
  }

  return {
    showEditProfileModal,
    selectedMember,
    needsRepublishing,

    acceptedCasters,
    activeTeam,
    canModifyCaster,
    casters,
    currentUserId,
    currentUserIsModerator,
    isCrewAllowed,
    onlineUsers,
    sourceStreams,
    streamsManager,
    token,
    visibleCasters,

    getProfile,
    getStream,
    editProfile,
    removeCaster,
    requestCasterRepublish,
    setNeedsRepublishing,
    updateTeamMember,
    canBeRemovedAsCaster
  }
}

export default useCastersPalette
